import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Divider, Link, Stack, Typography, styled } from '@mui/material';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { IUser, WorkspaceUser } from 'src/@types/user';
import { IWorkspace } from 'src/@types/workspace';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import { useLocales } from 'src/locales';
import { GuestChannelInfo } from 'src/requests/gathering';
import { SocketProvider } from 'src/socket';
import randomUUID from 'src/utils/uuidv4';
import * as Yup from 'yup';
import { JoinConferenceAuth } from './JoinConferenceAuth';

type FormValuesProps = {
  name: string;
  email: string;
};

type Props = {
  gathering: GuestChannelInfo;
  onSuccess(user: WorkspaceUser): void;
};

const CustomRHFTextField = styled(RHFTextField)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
}));

export function JoinConferenceGuestForm({ gathering, onSuccess }: Props) {
  const { translate } = useLocales();

  const handleOpenTerms = (type: 'terms' | 'privacy') =>
    type === 'terms' ? 'https://copera.ai/tos' : 'https://copera.ai/privacy';

  const GuestFormSchema = Yup.object().shape({
    name: Yup.string().required(translate('required shared.yup.name_required')),
    email: Yup.string()
      .email()
      .required(translate('required shared.yup.email_required')),
  });

  const guestUser: IUser = useMemo(() => {
    const sessionUserString = sessionStorage.getItem('guest_user');
    if (!sessionUserString) return { name: '', email: '' };
    return JSON.parse(sessionUserString);
  }, []);

  const defaultValues = {
    name: guestUser.name,
    email: guestUser.email,
  };

  const methods = useForm({
    resolver: yupResolver(GuestFormSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = methods;

  const onSubmit = async (data: FormValuesProps) => {
    try {
      const { name, email } = data;

      const guestId = randomUUID();

      const user = {
        _id: guestId,
        email,
        name,
        active: true,
        workspace: {
          _id: gathering.workspace,
        } as IWorkspace,
      };
      reset();
      onSuccess(user);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Stack sx={{ textAlign: 'center' }}>
      <SocketProvider namespace="public">
        <JoinConferenceAuth googleGuestLogin={onSubmit} />
      </SocketProvider>
      <Divider sx={{ my: 4 }}>
        <Typography variant="caption" color="text.secondary">
          {translate('shared.labels.or')}
        </Typography>
      </Divider>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={3} alignItems="flex-end">
          <CustomRHFTextField
            name="name"
            type="text"
            variant="outlined"
            label={translate('shared.labels.enter_your_name')}
          />

          <CustomRHFTextField
            name="email"
            type="email"
            label={translate('shared.labels.enter_your_email')}
          />

          <LoadingButton
            type="submit"
            variant="contained"
            loading={isSubmitting}
            disabled={!isValid}
            size="large"
            sx={{ width: '100%' }}
          >
            {translate('communicate.meet.join_meeting')}
          </LoadingButton>
          <Typography
            sx={{
              color: 'text.secondary',
              typography: 'caption',
              textAlign: 'center',
            }}
          >
            {translate('communicate.meet.by_using_copera_meetings_accept')}{' '}
            <Link
              underline="always"
              sx={{ cursor: 'pointer' }}
              href={handleOpenTerms('terms')}
            >
              {translate('signup.terms_of_service')}
            </Link>{' '}
            {translate('shared.labels.and')}{' '}
            <Link
              underline="always"
              sx={{ cursor: 'pointer' }}
              href={handleOpenTerms('privacy')}
            >
              {translate('signup.privacy_policy')}
            </Link>
            .
          </Typography>
        </Stack>
      </FormProvider>
    </Stack>
  );
}
